import BigNumber from 'bignumber.js';
import { format } from 'date-fns';
import { MAX_DECIMALS_DISPLAY } from 'src/constants';
import mixpanel from 'mixpanel-browser';

export const rangeFromZero = (len: number) => [...Array(len).keys()];

function getNonNegativeZero(value: string) {
  if (value === '-0') {
    return '0';
  }
  return value;
}

export const formatUnits = (
  value: BigNumber | number | string | bigint,
  decimals?: string | number
) => {
  return BigNumber(value)
    .div(`1e${decimals ?? 18}`)
    .toFixed();
};

export function formatDecimal(
  value: BigNumber | number | string | bigint,
  options?: {
    maxDecimals?: number;
    dynamicDecimals?: number;
    withTruncate?: false;
    shortFormat?: boolean;
    roundingMode?: BigNumber.RoundingMode;
  }
): string;
export function formatDecimal(
  value: BigNumber | number | string | bigint,
  options: {
    maxDecimals?: number;
    dynamicDecimals?: number;
    withTruncate: true;
    shortFormat?: boolean;
    roundingMode?: BigNumber.RoundingMode;
  }
): string | React.ReactElement;
export function formatDecimal(
  value: BigNumber | number | string | bigint,
  options?: {
    maxDecimals?: number;
    dynamicDecimals?: number;
    withTruncate?: boolean;
    shortFormat?: boolean;
    roundingMode?: BigNumber.RoundingMode;
  }
): string | React.ReactElement {
  const valueBn = BigNumber(String(value));
  const {
    maxDecimals: _maxDecimals,
    dynamicDecimals = 3,
    withTruncate = false,
    shortFormat = true,
    roundingMode,
  } = options || {};

  let numOfZeroesAfterDot = 0;
  const decimalValue = valueBn.toFixed().split('.')[1];

  if (dynamicDecimals && decimalValue) {
    for (let i = 0; i < decimalValue.length; i++) {
      if (decimalValue[i] === '0') {
        numOfZeroesAfterDot++;
      } else {
        break;
      }
    }
  }

  const maxDecimals =
    typeof _maxDecimals !== 'undefined'
      ? _maxDecimals
      : dynamicDecimals && decimalValue
        ? numOfZeroesAfterDot + dynamicDecimals
        : MAX_DECIMALS_DISPLAY;

  if (!valueBn.isFinite()) {
    return '--';
  }

  if (shortFormat && valueBn.gte(100_000)) {
    if (valueBn.gte(1_000_000_000_000_000_000)) {
      // return e notation
      return valueBn.toExponential(2);
    }
    // billions
    if (valueBn.gte(1_000_000_000)) {
      return `${valueBn.div(1_000_000_000).toFormat(0)}B`;
    }
    // millions
    if (valueBn.gte(1_000_000)) {
      return `${valueBn.div(1_000_000).toFormat(0)}M`;
    }
    // thousands
    return `${valueBn.div(1000).toFormat(0)}k`;
  }

  const formatted = valueBn.toFormat(maxDecimals, roundingMode);
  const shouldTruncate = numOfZeroesAfterDot > 5;

  const finalValue = !formatted.includes('.')
    ? getNonNegativeZero(formatted)
    : getNonNegativeZero(formatted.replace(/0+$/, '').replace(/\.$/, ''));

  let displayValue = finalValue;

  if (withTruncate && shouldTruncate) {
    const [beforeDecimal, afterDecimal] = finalValue.split('.');

    if (afterDecimal) {
      displayValue = `${beforeDecimal}.0${afterDecimal.slice(numOfZeroesAfterDot)}`;
    }
    return (
      <span title={shouldTruncate ? finalValue : undefined}>
        {beforeDecimal}.0
        <sub>{numOfZeroesAfterDot - 1}</sub>
        {afterDecimal.slice(numOfZeroesAfterDot)}
      </span>
    );
  }

  return finalValue;
}

export const formatDate = (date: Date | string | number) => {
  return format(date, 'MMM dd, yyyy, HH:mm:ss');
};

export const shortenAddress = (address: string, chars = 4) => {
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
};

// https://github.com/s-yadav/react-number-format/blob/7a373b97b1ad240a4a9d21661837518d186bd427/src/utils.tsx#L112
export function fixLeadingZero(numStr?: string) {
  if (!numStr) return numStr;
  const isNegative = numStr[0] === '-';
  if (isNegative) numStr = numStr.substring(1, numStr.length);
  const parts = numStr.split('.');
  const beforeDecimal = parts[0].replace(/^0+/, '') || '0';
  const afterDecimal = parts[1] || '';

  return `${isNegative ? '-' : ''}${beforeDecimal}${afterDecimal ? `.${afterDecimal}` : ''}`;
}

export function trackSCWriteEvent(
  eventName: string,
  _config: any,
  originalGasEstimate?: BigInt
) {
  const config = _config?.request;
  mixpanel.track(eventName, {
    account: config.account.address,
    args: config.args.map(String),
    gas: String(config.gas),
    value: String(config.value),
    originalGasEstimate: String(originalGasEstimate),
  });
}

export const doFetch = async (url: string, fallback?: any) => {
  try {
    const response = await fetch(url);
    const json = await response.json();

    if (json.error) {
      if (fallback) {
        return fallback;
      }
      throw new Error(json.error.message);
    }

    return json;
  } catch (e) {
    if (fallback) {
      return fallback;
    }

    throw e instanceof Error ? e : new Error(String(e));
  }
};

export const buildQueryParams = (params: Record<string, any>) => {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
};
